// src/components/Consulting/ConsultingPage.js
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from '../ConsultingPage.module.css';
import ParticleNetwork from './ParticleNetwork';
import Seo from './Seo';

/* ─── Service Pillars ─── */
const PILLARS = [
  {
    id: 'digital',
    icon: '⚡',
    accent: '#4361ee',
    title: 'Digital Transformation',
    tagline: 'Modernise your operations, end to end.',
    desc: 'We help industrial and mid-market businesses replace legacy processes with scalable digital infrastructure — from ERP selection and integration to custom software rollouts, IoT enablement and data dashboards your team will actually use.',
    services: [
      'ERP & WMS Selection & Implementation',
      'Process Automation & Workflow Digitisation',
      'Custom Software & Dashboard Development',
      'IoT & Sensor Integration',
      'Cloud Migration Strategy',
      'Change Management & Team Training',
    ],
  },
  {
    id: 'product',
    icon: '🧭',
    accent: '#7209b7',
    title: 'Product & Startup Advisory',
    tagline: 'From idea to scalable product.',
    desc: 'Drawing on years of building real products — including live fintech and sports platforms — we advise founders and product teams on architecture decisions, go-to-market strategy, hiring their first engineering team, and avoiding the costly mistakes most startups make in year one.',
    services: [
      'Fractional CTO / CPO Engagements',
      'Product-Market Fit Workshops',
      'Tech Stack & Architecture Reviews',
      'MVP Scoping & Roadmapping',
      'Investor-Ready Technical Due Diligence',
      'Engineering Team Building & Hiring',
    ],
  },
  {
    id: 'supply',
    icon: '🔗',
    accent: '#0096c7',
    title: 'Supply Chain & Procurement',
    tagline: 'Leaner, smarter, more resilient supply chains.',
    desc: 'Leveraging our own industrial supply chain experience, we help exporters, manufacturers and logistics operators reduce procurement costs, achieve compliance with global packaging and moisture standards, and build supplier networks that hold up under pressure.',
    services: [
      'Procurement Strategy & Vendor Consolidation',
      'Export Packaging & Compliance Advisory',
      'Moisture & Humidity Control Standards (MIL / ISO)',
      'Supplier Audit & Qualification Programs',
      'Inventory Optimisation',
      'Last-mile & Container Logistics Planning',
    ],
  },
];

/* ─── Differentiators ─── */
const DIFF = [
  { num: '01', title: 'Operators, Not Just Advisors', body: 'We run our own tech products and industrial supply chain. Our advice is grounded in what actually works at scale — not just frameworks from a slide deck.' },
  { num: '02', title: 'Tech-First by Default', body: 'Every engagement is backed by BETA Edge\'s in-house software capability. We don\'t just recommend tools — we can build them.' },
  { num: '03', title: 'Structured Engagements', body: 'Clearly scoped projects with defined deliverables, no ambiguous retainers that drag on indefinitely. You always know what you\'re getting.' },
  { num: '04', title: 'Cross-Vertical Perspective', body: 'With depth in both software and industrial sectors, we bring cross-domain insight that single-vertical consultancies miss entirely.' },
];

/* ─── Engagement Models ─── */
const ENGAGEMENTS = [
  {
    icon: '📋',
    title: 'Project-Based',
    best: 'Best for: Defined problems with clear outcomes',
    desc: 'Fixed scope, timeline and fee. Ideal for audits, strategy decks, tech assessments or a single-phase implementation.',
  },
  {
    icon: '🤝',
    title: 'Retainer Advisory',
    best: 'Best for: Ongoing strategic guidance',
    desc: 'A set number of hours per month — your team gets direct access to our senior consultants for questions, reviews and decision support.',
  },
  {
    icon: '🧑‍💼',
    title: 'Embedded Consulting',
    best: 'Best for: Complex transformations',
    desc: 'Our consultants work inside your organisation part-time or full-time for the duration of the engagement, ensuring real execution, not just recommendations.',
  },
];

/* ─── Industries ─── */
const INDUSTRIES = [
  { icon: '🏭', label: 'Manufacturing' },
  { icon: '📦', label: 'Logistics & Exports' },
  { icon: '🚀', label: 'Tech Startups' },
  { icon: '💊', label: 'Pharma & Healthcare' },
  { icon: '🌾', label: 'Agro & Food Processing' },
  { icon: '🛒', label: 'D2C & E-Commerce' },
  { icon: '🏗️', label: 'Infrastructure & Real Estate' },
  { icon: '⚙️', label: 'Industrial Equipment' },
];

/* ─── Main Page ─── */
const ConsultingPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('digital');
  const servicesRef = useRef(null);
  const whyRef = useRef(null);
  const engageRef = useRef(null);
  const contactRef = useRef(null);

  const scrollTo = (ref) => { ref.current?.scrollIntoView({ behavior: 'smooth' }); setMenuOpen(false); };
  const activePillar = PILLARS.find(p => p.id === activeTab);

  return (
    <div className={styles.page}>
      <Seo
        title="BETA Edge Consulting | Digital Transformation, Product Advisory & Supply Chain Strategy India"
        description="BETA Edge Consulting delivers Digital Transformation, Product & Startup Advisory and Supply Chain consulting for clients across India, Singapore, New Zealand, Japan, Malaysia, the UK, Canada and the USA."
        keywords="digital transformation consulting India, startup advisory services, product consulting India, supply chain consulting, technology consulting Kolkata, fractional CTO India, ERP consulting, warehouse management consulting, business strategy consulting India, IT consulting services"
        canonical="https://www.beta-edge.com/consulting"
        ogTitle="BETA Edge Consulting — Digital Transformation & Business Advisory India"
        ogDescription="Tech-first consulting across Digital Transformation, Product Advisory and Supply Chain Strategy. Rated 5 stars by clients worldwide."
        ogUrl="https://www.beta-edge.com/consulting"
        ogImage="https://www.beta-edge.com/og-consulting.jpg"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'ProfessionalService',
            name: 'BETA Edge Consulting',
            url: 'https://www.beta-edge.com/consulting',
            description: 'Digital transformation, product advisory and supply chain consulting firm serving startups, manufacturers and exporters.',
            areaServed: ['IN', 'SG', 'NZ', 'JP', 'MY', 'GB', 'CA', 'US'],
            serviceType: PILLARS.map((p) => p.title),
            address: { '@type': 'PostalAddress', addressLocality: 'Kolkata', addressCountry: 'IN' },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '5.0',
              bestRating: '5',
              reviewCount: '15',
            },
          },
        ]}
      />
      <ParticleNetwork
        dotColor="#4361ee"
        lineColor="67, 97, 238"
        cursorLineColor="140, 160, 255"
      />

      {/* ── Nav ── */}
      <nav className={styles.nav} aria-label="BETA Edge Consulting navigation">
        <div className={styles.navBrand}>
          <Link to="/" className={styles.brandBack}>← Tech</Link>
          <span className={styles.brandDivider}>|</span>
          <Link to="/industrial" className={styles.brandSibling}>Industrial</Link>
          <span className={styles.brandDivider}>|</span>
          <span className={styles.brandName}>BETA Edge <span className={styles.brandTag}>Consulting</span></span>
        </div>
        <button className={styles.burger} aria-label="Toggle menu" onClick={() => setMenuOpen(p => !p)}>☰</button>
        <div className={`${styles.navMenu} ${menuOpen ? styles.navMenuOpen : ''}`}>
          <button onClick={() => scrollTo(servicesRef)}>Services</button>
          <button onClick={() => scrollTo(whyRef)}>Why Us</button>
          <button onClick={() => scrollTo(engageRef)}>Engagement Models</button>
          <a href="mailto:connect@beta-edge.com" className={styles.navCta}>Start a Conversation</a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className={styles.hero}>
        <div className={styles.heroBg} aria-hidden="true" />
        <div className={styles.heroContent}>
          <p className={styles.heroEyebrow}>BETA Edge Consulting Division</p>
          <h1 className={styles.heroHeadline}>
            Business Consulting Services<br />
            <span className={styles.heroAccent}>India — Strategy &amp; Technology</span>
          </h1>
          <p className={styles.heroSub}>
            We are operators who consult — not consultants who theorise. BETA Edge Consulting combines deep software engineering, real product-building experience and hands-on industrial supply chain knowledge to deliver advice that actually moves the needle.
          </p>
          <div className={styles.heroPillars}>
            {PILLARS.map(p => (
              <div key={p.id} className={styles.heroPillar} style={{ '--accent': p.accent }}>
                <span className={styles.heroPillarIcon}>{p.icon}</span>
                <span>{p.title}</span>
              </div>
            ))}
          </div>
          <div className={styles.heroActions}>
            <button className={styles.heroCta} onClick={() => scrollTo(servicesRef)}>Explore Services ↓</button>
            <a href="mailto:connect@beta-edge.com" className={styles.heroSecondary}>Book a Free Discovery Call</a>
          </div>
        </div>
      </header>

      {/* ── Industry Strip ── */}
      <section className={styles.indStrip} aria-label="Industries we consult">
        <div className={styles.sectionInner}>
          <p className={styles.striplabel}>Sectors We Serve</p>
          <div className={styles.chipRow}>
            {INDUSTRIES.map(i => (
              <div key={i.label} className={styles.chip}>
                <span>{i.icon}</span><span>{i.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services / Pillars ── */}
      <section className={styles.services} ref={servicesRef} id="services">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>What We Do</p>
          <h2 className={styles.sectionTitle}>Three Consulting Practices Under One Roof</h2>
          <p className={styles.sectionSub}>
            Most consultancies specialise in one lane. BETA Edge Consulting bridges strategy, technology and operations — so you get joined-up advice without stitching together three separate firms.
          </p>

          {/* Tab selector */}
          <div className={styles.tabs} role="tablist">
            {PILLARS.map(p => (
              <button
                key={p.id}
                role="tab"
                aria-selected={activeTab === p.id}
                className={`${styles.tab} ${activeTab === p.id ? styles.tabActive : ''}`}
                style={activeTab === p.id ? { '--tab-accent': p.accent } : {}}
                onClick={() => setActiveTab(p.id)}
              >
                <span>{p.icon}</span> {p.title}
              </button>
            ))}
          </div>

          {/* Active pillar detail */}
          <div className={styles.pillarPanel} style={{ '--panel-accent': activePillar.accent }}>
            <div className={styles.pillarLeft}>
              <p className={styles.pillarTagline}>{activePillar.tagline}</p>
              <p className={styles.pillarDesc}>{activePillar.desc}</p>
              <a href="mailto:connect@beta-edge.com" className={styles.pillarCta}>
                Discuss This Practice →
              </a>
            </div>
            <div className={styles.pillarRight}>
              <p className={styles.pillarServicesLabel}>What's Included</p>
              <ul className={styles.pillarServices}>
                {activePillar.services.map(s => (
                  <li key={s}><span className={styles.check}>✓</span>{s}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why BETA Edge Consulting ── */}
      <section className={styles.why} ref={whyRef} id="why-us">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>Our Edge</p>
          <h2 className={styles.sectionTitle}>Why BETA Edge Consulting Is Different</h2>
          <div className={styles.diffGrid}>
            {DIFF.map(d => (
              <div key={d.num} className={styles.diffCard}>
                <span className={styles.diffNum}>{d.num}</span>
                <h3>{d.title}</h3>
                <p>{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Engagement Models ── */}
      <section className={styles.engage} ref={engageRef} id="engagement">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>How We Work</p>
          <h2 className={styles.sectionTitle}>Engagement Models</h2>
          <p className={styles.sectionSub}>
            We adapt to what suits your business — whether you need a focused two-week audit or a long-term strategic partner embedded in your team.
          </p>
          <div className={styles.engageGrid}>
            {ENGAGEMENTS.map(e => (
              <div key={e.title} className={styles.engageCard}>
                <div className={styles.engageIcon}>{e.icon}</div>
                <h3>{e.title}</h3>
                <p className={styles.engageBest}>{e.best}</p>
                <p className={styles.engageDesc}>{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className={styles.ctaBanner} ref={contactRef}>
        <div className={styles.ctaInner}>
          <div>
            <h2>Ready to Start a Conversation?</h2>
            <p>Tell us about your challenge. The first discovery call is always free and commitment-free.</p>
          </div>
          <div className={styles.ctaButtons}>
            <a href="mailto:connect@beta-edge.com" className={styles.ctaPrimary}>Email Us Directly</a>
            <a href="tel:+919804619202" className={styles.ctaOutline}>Call (+91) 9804 619 202</a>
          </div>
        </div>
      </section>

      {/* ── Contact Strip ── */}
      <section className={styles.contactStrip}>
        <div className={styles.stripInner}>
          <div className={styles.stripItem}><span>📍</span><div><strong>Base</strong><p>Kolkata, India</p></div></div>
          <div className={styles.stripItem}><span>📞</span><div><strong>Call / WhatsApp</strong><p><a href="tel:+919804619202">(+91) 9804 619 202</a></p><p><a href="tel:+6591673307">(+65) 9167 3307</a></p></div></div>
          <div className={styles.stripItem}><span>✉️</span><div><strong>Email</strong><p><a href="mailto:connect@beta-edge.com">connect@beta-edge.com</a></p></div></div>
          <div className={styles.stripItem}><span>🌐</span><div><strong>Delivery Presence</strong><p>India · Singapore · Taiwan · Hong Kong</p></div></div>
        </div>
      </section>

      {/* ── Cross-Division Links (SEO internal linking) ── */}
      <section className={styles.crossLinks} aria-label="Other BETA Edge divisions">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>Also from BETA Edge</p>
          <div className={styles.crossGrid}>
            <Link to="/" className={styles.crossCard}>
              <span className={styles.crossIcon}>💻</span>
              <div>
                <strong>Software &amp; App Development</strong>
                <p>Custom web and mobile applications, SaaS platforms and digital product development for businesses across India and Singapore.</p>
                <span className={styles.crossCta}>Explore Tech Services →</span>
              </div>
            </Link>
            <Link to="/industrial" className={styles.crossCard}>
              <span className={styles.crossIcon}>🏭</span>
              <div>
                <strong>Industrial Moisture Absorbent Products</strong>
                <p>Silica gel desiccants, container desiccants, moisture barrier bags and humidity control solutions for manufacturers and exporters.</p>
                <span className={styles.crossCta}>Explore Industrial →</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className={styles.footer}>
        <p>
          © {new Date().getFullYear()} <strong>BETA Edge Consulting</strong> — A division of BETA Edge Inc.
          &nbsp;|&nbsp; <Link to="/">Tech & Software</Link>
          &nbsp;|&nbsp; <Link to="/industrial">Industrial</Link>
        </p>
        <p className={styles.footerKeywords}>
          Digital Transformation Consulting India · Product Advisory · Fractional CTO · Supply Chain Consulting · IT Strategy
        </p>
      </footer>
    </div>
  );
};

export default ConsultingPage;